package com.eladfinish.bible.contest.screens.main.ui

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.LocalTextStyle
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.platform.UriHandler
import androidx.compose.ui.text.LinkAnnotation
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.TextLinkStyles
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.text.withLink
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.eladfinish.bible.contest.appcontent.AboutContent
import com.eladfinish.bible.contest.appcontent.AppLinks
import com.eladfinish.bible.contest.di.config.AppInfo
import com.eladfinish.bible.contest.extensions.formatString
import com.eladfinish.bible.contest.features.hebrewCalendar.GetTodayHebrewDateUseCase
import com.eladfinish.bible.contest.features.teruma.AboutTerumaUiModel
import io.github.aakira.napier.Napier

@Composable
fun AboutPageContent(
    appInfo: AppInfo,
    aboutTerumaUiModel: AboutTerumaUiModel?,
    logEvent: (String) -> Unit,
) {
    val uriHandler = LocalUriHandler.current

    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(horizontal = 10.dp),
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        AboutText(
            text = GetTodayHebrewDateUseCase().invoke(),
            style = MaterialTheme.typography.titleMedium,
        )
        AboutText(
            text = """אפליקציית "הראשון בחידון" - מהדורה חדשה ללא פרסומות!""",
            style = MaterialTheme.typography.titleLarge,
            modifier = Modifier.padding(top = 8.dp)
        )
        ClickableAboutText(
            modifier = Modifier.padding(top = 8.dp),
            uriHandler = uriHandler,
            plainText = "© כל הזכויות שמורות למְפתח\n",
            clickableText = AboutContent.DEVELOPER_NAME,
            url = AppLinks.DEVELOPER_PAGE_URL, // https://play.google.com/store/search?q=pub:Elad%20Finish
            fontSize = 24.sp,
            logEvent = logEvent,
        )
        AboutContent.achievements.forEach { achievement ->
            AboutText(
                text = achievement,
                style = MaterialTheme.typography.titleLarge,
                modifier = Modifier.padding(top = 8.dp)
            )
        }
        AboutContent.thanks.forEachIndexed { index, thanks ->
            AboutText(
                text = thanks,
                style = MaterialTheme.typography.titleMedium,
                modifier = Modifier.padding(top = if (index == 0) 16.dp else 8.dp)
            )
        }
        AboutText(
            text = "נהניתם? החכמתם? אנא דרגו וספרו לחבריכם!",
            style = MaterialTheme.typography.titleLarge,
            modifier = Modifier.padding(top = 8.dp)
        )
        AboutText(
            text = AboutContent.FEEDBACK_PROMPT,
            style = MaterialTheme.typography.titleLarge,
            modifier = Modifier.padding(top = 8.dp)
        )

        // TODO: fix in iOS
        // https://stackoverflow.com/questions/33694771/open-email-from-app-with-predefined-text-in-ios
        val myEmail = AppLinks.CONTACT_EMAIL
        ClickableAboutText(
            modifier = Modifier.padding(top = 8.dp),
            uriHandler = uriHandler,
            clickableText = myEmail,
            url = "mailto:%s?subject=%s&body=%s\n%s".formatString(
                myEmail,
                "אפליקציית הראשון בחידון",
                "מידע זה יסייע לתיקון הבאגים:",
                appInfo.information,
            ),
            fontSize = 20.sp,
            logEvent = logEvent,
        )

        ClickableAboutText(
            modifier = Modifier.padding(top = 8.dp),
            uriHandler = uriHandler,
            plainText = "לשם הצגת התאריך העברי ולמציאת פרשת השבוע בשבת הקרובה נעשה שימוש בספריית\n",
            clickableText = "KosherJava Zmanim",
            url = "http://www.kosherjava.com/zmanim-project/",
            logEvent = logEvent,
        )

        ClickableAboutText(
            modifier = Modifier.padding(top = 8.dp),
            uriHandler = uriHandler,
            plainText = "תחת הרישיון של ",
            clickableText = "LGPL 2.1",
            url = "https://www.gnu.org/licenses/old-licenses/lgpl-2.1.html",
            logEvent = logEvent,
        )

        AboutText(
            text = """כדי שאוכל להמשיך לפתח, לתחזק ולהפעיל את אפליקציית ״הראשון בחידון״ בחינם עבור כולם — כולל מימון שרתים ושירותי בינה מלאכותית מתקדמים — הוכנסו פרסומות לאפליקציה, תוך השתדלות לשמור עליהן מינימליות ולא לפגוע בחוויית השימוש. אם נתקלתם בפרסומת שאינה ראויה או שאינה מתאימה לרוח האפליקציה, אשמח שתדווחו לי ואשתדל להסיר את המפרסם. תודה רבה על ההבנה והתמיכה ❤️""",
            style = MaterialTheme.typography.titleMedium,
            modifier = Modifier.padding(top = 8.dp)
        )

        aboutTerumaUiModel?.let {
            ClickableAboutText(
                modifier = Modifier.padding(top = 8.dp),
                uriHandler = uriHandler,
                plainText = it.text.trim(),
                clickableText = it.ctaText,
                newLineForClickableText = true,
                url = it.url,
                logEvent = logEvent,
            )
        }
    }
}

@Composable
private fun AboutText(
    text: String,
    modifier: Modifier = Modifier,
    style: TextStyle = LocalTextStyle.current,
) {
    Text(
        text = text,
        style = style,
        textAlign = TextAlign.Center,
        fontWeight = FontWeight.Bold,
        modifier = modifier,
    )
}

@Composable
private fun ClickableAboutText(
    modifier: Modifier = Modifier,
    uriHandler: UriHandler,
    plainText: String = "",
    clickableText: String,
    newLineForClickableText: Boolean = false,
    url: String,
    fontSize: TextUnit = 16.sp,
    logEvent: (String) -> Unit,
) {
    val annotated = buildAnnotatedString {
        append(plainText)

        if (newLineForClickableText) {
            append("\n")
        }

        // Create a real link range using LinkAnnotation (replacement for ClickableText + StringAnnotation).
        withLink(
            LinkAnnotation.Url(
                url = url,
                styles = TextLinkStyles(
                    style = SpanStyle(
                        textDecoration = TextDecoration.Underline,
                        color = Color.Blue,
                    )
                )
            ) { link ->
                // link is the LinkAnnotation that was clicked (typically LinkAnnotation.Url here).
                try {
                    logEvent("מסך אודות: $clickableText")
                    val clickedUrl = (link as? LinkAnnotation.Url)?.url ?: url
                    uriHandler.openUri(clickedUrl)
                } catch (e: Exception) {
                    Napier.e("LinkAnnotation click failed: $e")
                    e.printStackTrace()
                }

                Napier.d("LinkAnnotation.Url = ${(link as? LinkAnnotation.Url)?.url}")
            }
        ) {
            append(clickableText)
        }
    }

    Text(
        modifier = modifier,
        text = annotated,
        style = MaterialTheme.typography.titleMedium.copy(
            fontSize = fontSize,
            fontWeight = FontWeight.Bold,
            textAlign = TextAlign.Center,
        ),
    )
}
